import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as Doc } from 'assets/icons/file.svg';
import { ReactComponent as FileDownloadIcon } from 'assets/icons/file-download.svg';
import AdminAvatar from 'assets/icons/superadmin/support-chat.svg';
import {
  Attachment,
  ContentType,
  InMailMessageOutput,
  InMailParticipantOutput,
} from 'graphql/generated/hasura';
import { twMerge } from 'tailwind-merge';
import Avatar from 'components/avatarComponent';
import moment from 'moment';
import { FULL_DATE } from 'utilities/dateHelper';
import { useGetSignedUrlFromStorage } from 'hooks/useGetSignedUrlFromStorage';
import ChatMediaModal from '../../../../my-appointments/components/ChatMediaModal';
import ButtonComponent from 'components/button/buttonComponent';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import { formatFileSize } from 'utilities/fileHelpers';
import { useAuth } from 'auth/context/AuthContext';
import ImageMiniComponent from 'components/ImageMiniComponent';

interface IMessageProps extends InMailMessageOutput {
  sender?: InMailParticipantOutput;
  isProvider: boolean;
  isSupportChat?: boolean;
  dateFormat?: string;
  userClassName?: string;
  textClassName?: string;
}

interface FileItemProps extends Attachment {
  onDownloadFile: () => void;
  disabled: boolean;
}

const IMAGE_TYPES = [ContentType.Png, ContentType.Jpeg, ContentType.Jpg];

const FileItem: React.FC<FileItemProps> = ({
  size,
  title,
  contentType,
  onDownloadFile,
  disabled = false,
}) => {
  return (
    <div className="flex flex-row justify-between border border-gray-opacity-15 desktop:rounded-lg rounded desktop:items-center w-full gap-2.5 desktop:py-2.5 desktop:px-5 py-1 px-2">
      <div className="flex items-center desktop:gap-2.5">
        <div className="text-black">
          <Doc className={'w-6 h-6 max-desktop:hidden'} />
        </div>
        <div className="flex flex-col text-neutral-800 font-semibold">
          <div className="truncate desktop:max-w-[220px] max-w-[160px]">
            {title}
          </div>
          <div className="font-medium text-sm">
            {contentType?.toLowerCase()} •{' '}
            {size ? formatFileSize(size) : '0 Kb'}
          </div>
        </div>
      </div>

      <div className="cursor-pointer">
        <ButtonComponent
          onClick={() => onDownloadFile()}
          type={'icon'}
          testID={'download-file-button'}
          Icon={FileDownloadIcon}
          iconHeight="w-4 h-4 max-w-[16px]"
          iconPosition="left"
          className="flex items-center bg-transparent gap-2.5 !p-0 !text-xs !w-auto"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const AttachmentBlock: React.FC<Attachment> = ({
  url,
  contentType,
  size,
  title,
}) => {
  const { getProxyUrlFileByUrl } = useGetFileFromStorage();
  const [loading, setLoading] = useState<boolean>(false);
  const [src, setSrc] = useState<string | null | undefined>(null);
  const [srcFull, setSrcFull] = useState<string | null | undefined>(null);

  const mediaId = useMemo(() => {
    const split = url?.split('/');
    return split?.[split?.length - 1];
  }, [url]);

  const { downloadFileFromUrl } = useGetFileFromStorage();

  const onDownloadFile = useCallback(() => {
    return downloadFileFromUrl(src as string);
  }, [downloadFileFromUrl, src]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const src = await getProxyUrlFileByUrl(url as string);
      setSrc(src);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  const reschedule = async (mediaId?: string) => {
    if (!mediaId) {
      return;
    }
    return await getProxyUrlFileByUrl(url as string);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (IMAGE_TYPES.includes(contentType as ContentType)) {
    return (
      <>
        <ChatMediaModal title="Photo" src={srcFull} setSrc={setSrcFull} />
        <ImageMiniComponent
          altText={'mediaAlt'}
          initialMediaUrl={url ?? ''}
          reschedule={reschedule}
          setSrc={setSrcFull}
          mediaId={mediaId}
          maxRetry={2}
          className="desktop:max-w-[240px] cursor-pointer max-w-[160px] rounded-5"
        />
      </>
    );
  }

  return (
    <div className="bg-base-content rounded-5">
      <FileItem
        onDownloadFile={onDownloadFile}
        disabled={loading}
        contentType={contentType}
        title={title}
        size={size}
      />
    </div>
  );
};

const Message: React.FC<IMessageProps> = ({
  date,
  sender,
  senderCodexId,
  text,
  isProvider,
  isSupportChat = false,
  attachments,
  dateFormat = FULL_DATE,
  userClassName,
  textClassName,
}) => {
  const { user } = useAuth();
  const signedProfileUrl = useGetSignedUrlFromStorage(
    sender?.profilePictureId ?? '',
    sender?.codexId === user?.uuid ? undefined : sender?.codexId,
  );

  const isAdmin = isSupportChat && senderCodexId !== sender?.codexId;

  return (
    <div
      className={twMerge(
        'flex gap-4 ml-auto desktop:max-w-[75%] items-end',
        isProvider && 'ml-0 mr-auto flex-row-reverse',
      )}
    >
      <div
        className={twMerge(
          'flex flex-col gap-2.5 p-4 bg-white rounded-2xl text-neutral-800 font-medium whitespace-pre-wrap',
          isProvider ? 'rounded-bl-none' : 'rounded-br-none',
          isProvider ? textClassName : userClassName,
        )}
      >
        {attachments.map((attach) => (
          <AttachmentBlock key={attach.url} {...attach} />
        ))}
        <p className="[word-break:break-word] break-words whitespace-pre-wrap">
          {text}
        </p>
        <p
          className={twMerge(
            'text-xs text-med-gray-3 font-semibold',
            !isProvider && userClassName,
          )}
        >
          {moment(date).format(dateFormat)}
        </p>
      </div>
      <Avatar
        size="sm"
        customClasses="h-8 min-w-[2rem] w-8"
        imageUrl={isAdmin ? AdminAvatar : signedProfileUrl}
        key={signedProfileUrl}
      />
    </div>
  );
};

export default Message;
